import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import './FinancingSimulator.css'
import { simulatePrice, simulateSac, totalsOf } from './utils/financingSimulator'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const formatBRL = (value) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const FinancingSimulator = () => {
  useDocumentMeta({
    title: 'Simulador de Financiamento Price vs SAC | jarder.dev',
    description:
      'Compare as tabelas Price e SAC com amortização mês a mês completa. ' +
      'Grátis, direto no navegador.',
  })

  const [principal, setPrincipal] = useState('200000')
  const [rate, setRate] = useState('1')
  const [months, setMonths] = useState('360')
  const [showAll, setShowAll] = useState(false)

  const data = useMemo(() => {
    const principalNum = parseFloat(principal.replace(',', '.'))
    const rateNum = parseFloat(rate.replace(',', '.'))
    const monthsNum = Number.parseInt(months, 10)
    if (!principalNum || !rateNum || !monthsNum) return null
    const monthlyRate = rateNum / 100
    const priceRows = simulatePrice({ principal: principalNum, monthlyRate, months: monthsNum })
    const sacRows = simulateSac({ principal: principalNum, monthlyRate, months: monthsNum })
    return {
      price: { rows: priceRows, totals: totalsOf(priceRows) },
      sac: { rows: sacRows, totals: totalsOf(sacRows) },
    }
  }, [principal, rate, months])

  const handleChange = () => trackEvent('tool_generate', { tool: 'financing_simulator' })

  const visibleRows = (rows) => (showAll ? rows : rows.slice(0, 12))

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Simulador de Financiamento Price vs SAC</h1>
      <p className="tool-disclaimer">
        Simulação simplificada, sem taxas administrativas ou seguros:
        bancos costumam cobrar outros valores além dos juros.
      </p>

      <div className="tool-row">
        <div className="tool-field">
          <label htmlFor="principal">Valor financiado (R$)</label>
          <input
            id="principal"
            type="text"
            inputMode="decimal"
            value={principal}
            onChange={(e) => setPrincipal(e.target.value)}
            onBlur={handleChange}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="rate">Taxa de juros mensal (%)</label>
          <input
            id="rate"
            type="text"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            onBlur={handleChange}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="months">Prazo (meses)</label>
          <input
            id="months"
            type="number"
            min="1"
            value={months}
            onChange={(e) => setMonths(e.target.value)}
            onBlur={handleChange}
          />
        </div>
      </div>

      {data && (
        <>
          <div className="financing-summary-row">
            <div className="tool-result-card">
              <span className="tool-result-label">Tabela Price: 1ª parcela</span>
              <span className="tool-result-value">{formatBRL(data.price.rows[0].installment)}</span>
              <span className="tool-import-hint">
                Total pago: {formatBRL(data.price.totals.totalPaid)} · Juros: {formatBRL(data.price.totals.totalInterest)}
              </span>
            </div>
            <div className="tool-result-card">
              <span className="tool-result-label">Tabela SAC: 1ª parcela</span>
              <span className="tool-result-value">{formatBRL(data.sac.rows[0].installment)}</span>
              <span className="tool-import-hint">
                Total pago: {formatBRL(data.sac.totals.totalPaid)} · Juros: {formatBRL(data.sac.totals.totalInterest)}
              </span>
            </div>
          </div>

          <button
            type="button"
            className="tool-button tool-button-secondary"
            onClick={() => setShowAll((v) => !v)}
            style={{ marginBottom: 16 }}
          >
            {showAll ? 'Mostrar só os primeiros 12 meses' : 'Mostrar tabela completa'}
          </button>

          <div className="financing-tables">
            <div className="financing-table-wrapper">
              <h3>Price</h3>
              <table className="financing-table">
                <thead>
                  <tr>
                    <th>Mês</th>
                    <th>Parcela</th>
                    <th>Juros</th>
                    <th>Amortização</th>
                    <th>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows(data.price.rows).map((row) => (
                    <tr key={row.month}>
                      <td>{row.month}</td>
                      <td>{formatBRL(row.installment)}</td>
                      <td>{formatBRL(row.interest)}</td>
                      <td>{formatBRL(row.amortization)}</td>
                      <td>{formatBRL(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="financing-table-wrapper">
              <h3>SAC</h3>
              <table className="financing-table">
                <thead>
                  <tr>
                    <th>Mês</th>
                    <th>Parcela</th>
                    <th>Juros</th>
                    <th>Amortização</th>
                    <th>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows(data.sac.rows).map((row) => (
                    <tr key={row.month}>
                      <td>{row.month}</td>
                      <td>{formatBRL(row.installment)}</td>
                      <td>{formatBRL(row.interest)}</td>
                      <td>{formatBRL(row.amortization)}</td>
                      <td>{formatBRL(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <div className="tool-info-section">
        <h2>Price ou SAC: qual a diferença</h2>
        <p>
          Na <strong>tabela Price</strong>, a parcela é fixa do início ao
          fim  no começo você paga mais juros e menos amortização, e
          essa proporção se inverte ao longo do tempo. Na{' '}
          <strong>tabela SAC</strong>, a amortização é fixa e os juros
          diminuem a cada mês, então as parcelas começam mais altas e vão
          caindo. No total, o SAC costuma gerar menos juros pagos, mas
          exige uma parcela inicial maior.
        </p>
      </div>
    </div>
  )
}

export default FinancingSimulator
